import React, { useEffect } from 'react';

import { IconButton, Typography } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useIntercom } from 'react-use-intercom';
import { AlineButton } from 'src/components';
import { INTERCOM_LAUNCHER_DEFAULTS } from 'src/components/intercom';
import { makeStyles } from 'tss-react/mui';

/** Reserved at the bottom of the page so the bar never covers the last section. */
export const LAUNCH_FOOTER_HEIGHT = 64;

const useStyles = makeStyles()(theme => ({
  content: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(2),
    height: LAUNCH_FOOTER_HEIGHT,
    justifyContent: 'space-between',
    padding: theme.spacing(0, 3),
  },
  jump: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  root: {
    background: theme.palette.common.white,
    borderTop: `1px solid ${theme.appColors.rule}`,
    bottom: 0,
    left: 0,
    position: 'fixed',
    right: 0,
    zIndex: theme.zIndex.appBar,
  },
}));

/**
 * Bar pinned to the bottom of the launch page: jump between required questions
 * still unanswered, and launch from anywhere in a long form.
 */
export function LaunchFooter({
  isSubmitting,
  onJumpToNextRequiredField,
  onJumpToPreviousRequiredField,
  requiredFieldCount,
}: {
  isSubmitting: boolean;
  onJumpToNextRequiredField: () => void;
  onJumpToPreviousRequiredField: () => void;
  requiredFieldCount: number;
}): JSX.Element {
  const { classes } = useStyles();
  const { update: updateIntercom } = useIntercom();
  const hasRequiredFields = requiredFieldCount > 0;

  /** Park the launcher bottom-left, above the footer, so it never covers the launch button. */
  useEffect(() => {
    updateIntercom({
      alignment: 'left',
      verticalPadding: LAUNCH_FOOTER_HEIGHT + INTERCOM_LAUNCHER_DEFAULTS.verticalPadding,
    });

    return () => updateIntercom(INTERCOM_LAUNCHER_DEFAULTS);
  }, [updateIntercom]);

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.jump}>
          <Typography color="textSecondary">
            {hasRequiredFields
              ? `Jump to required fields (${requiredFieldCount})`
              : 'All required fields filled in'}
          </Typography>
          <IconButton
            aria-label="Previous required field"
            color="primary"
            disabled={!hasRequiredFields}
            size="small"
            onClick={onJumpToPreviousRequiredField}
          >
            <ChevronUpIcon size={18} />
          </IconButton>
          <IconButton
            aria-label="Next required field"
            color="primary"
            disabled={!hasRequiredFields}
            size="small"
            onClick={onJumpToNextRequiredField}
          >
            <ChevronDownIcon size={18} />
          </IconButton>
        </div>
        <AlineButton
          color="primary"
          disabled={isSubmitting}
          loading={isSubmitting}
          type="submit"
          variant="contained"
        >
          Launch Workflow
        </AlineButton>
      </div>
    </div>
  );
}
